import { NavigationDot, useNavigationStatus } from '../connections/NavigationStatus';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';
import { favoritesQuery, useSetFavorite, conversationsQuery, dailyBriefQuery, keys, schemaQuery } from '../../lib/queries';
import type { ConversationSummary } from '../../lib/api-types';
import { subscribeToRecordCountIncreases } from '../../lib/record-count-events';
import { useTheme } from '../../lib/theme';
import { PageContextProvider } from '../../lib/page-context';
import { FloatingComposePanel } from '../chat/FloatingComposePanel';
import { Icon } from '../ui/Icon';
import { ResonanceMark } from '../ui/ResonanceMark';
import styles from './AppShell.module.css';

const RECENT_CHAT_LIMIT = 6;
const COUNT_HIGHLIGHT_MS = 1_600;
type DeleteChatState = {
  conversationId: string;
  status: 'confirming' | 'deleting' | 'error';
  error?: string;
};

export function AppShell({ children, railCollapsed = false }: { children: ReactNode; railCollapsed?: boolean }) {
  const [mobileMenu, setMobileMenu] = useState(false);
  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileMenu(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);
  return (
    <PageContextProvider>
      <div className={styles.shell} data-rail-collapsed={railCollapsed} data-mobile-menu={mobileMenu}
        onClick={event => { if ((event.target as HTMLElement).closest('a')) setMobileMenu(false); }}>
        <div className="appGradient" />
        <div className={styles.mobileBar}>
          <button type="button" aria-label={mobileMenu ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileMenu}
            onClick={() => setMobileMenu(current => !current)}><Icon name="menu-2" size={20}/><span>Menu</span></button>
          <span>Resonance</span>
        </div>
        {mobileMenu && <button className={styles.mobileBackdrop} type="button" aria-label="Close navigation overlay" onClick={() => setMobileMenu(false)}/>}
        <LeftRail />
        <main className={styles.main}>{children}</main>
        <FloatingComposePanel />
      </div>
    </PageContextProvider>
  );
}

function LeftRail() {
  const navigationStatus=useNavigationStatus();
  const schema = useQuery(schemaQuery());
  const favorites = useQuery(favoritesQuery());
  const setFavorite = useSetFavorite();
  const [showsAllFavorites, setShowsAllFavorites] = useState(false);
  const conversations = useQuery(conversationsQuery());
  const brief = useQuery(dailyBriefQuery());
  const { bootstrap } = useAuth();
  const { appearance, setAppearance } = useTheme();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showsAllChats, setShowsAllChats] = useState(false);
  const [deleteChatState, setDeleteChatState] = useState<DeleteChatState | null>(null);
  const [increasedObjectIds, setIncreasedObjectIds] = useState<Set<string>>(new Set());
  const previouslyStreaming = useRef(new Set<string>());
  const countHighlightTimers = useRef(new Map<string, number>());

  const deleteConversation = useMutation({ mutationFn: api.deleteConversation });

  // Objects come from the schema descriptor, so a custom object created in
  // chat appears in the rail as soon as the descriptor is refetched.
  const objects = schema.data?.objects ?? [];
  const hasFavorites = (favorites.data?.favorites.length ?? 0) > 0;
  const allChats = conversations.data?.conversations ?? [];
  const recentChats = allChats.slice(0, RECENT_CHAT_LIMIT);

  useEffect(() => {
    if (!hasFavorites) setShowsAllFavorites(false);
  }, [hasFavorites]);

  useEffect(() => {
    const current = new Set(allChats.filter((conversation) => conversation.isStreaming).map((conversation) => conversation.id));
    const finished = [...previouslyStreaming.current].some((id) => !current.has(id));
    previouslyStreaming.current = current;
    if (!finished) return;
    void queryClient.invalidateQueries({ queryKey: ['records'] });
    void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    void queryClient.invalidateQueries({ queryKey: keys.schema });
    void queryClient.invalidateQueries({ queryKey: keys.favorites });
    void queryClient.invalidateQueries({ queryKey: keys.dailyBrief });
  }, [allChats, queryClient]);

  useEffect(() => subscribeToRecordCountIncreases(({ objectId }) => {
    setIncreasedObjectIds((current) => new Set(current).add(objectId));
    const existing = countHighlightTimers.current.get(objectId);
    if (existing !== undefined) window.clearTimeout(existing);
    countHighlightTimers.current.set(objectId, window.setTimeout(() => {
      setIncreasedObjectIds((current) => {
        const next = new Set(current);
        next.delete(objectId);
        return next;
      });
      countHighlightTimers.current.delete(objectId);
    }, COUNT_HIGHLIGHT_MS));
  }), []);

  useEffect(() => () => {
    for (const timeout of countHighlightTimers.current.values()) window.clearTimeout(timeout);
  }, []);
  const accountName = [bootstrap?.user?.firstName, bootstrap?.user?.lastName]
    .filter(Boolean)
    .join(' ') || bootstrap?.workspace.displayName || 'Resonance';
  const accountSubtitle = bootstrap?.user?.email ?? bootstrap?.workspace.displayName ?? '';

  const handleDeleteConversation = async (conversation: ConversationSummary): Promise<void> => {
    const isCurrent = Boolean(
      matchRoute({ to: '/app/c/$conversationId', params: { conversationId: conversation.id } }),
    );
    setDeleteChatState({ conversationId: conversation.id, status: 'deleting' });
    try {
      await deleteConversation.mutateAsync(conversation.id);
      await queryClient.invalidateQueries({ queryKey: keys.conversations });
      setDeleteChatState(null);

      if (isCurrent) {
        const replacement = allChats.find((candidate) => candidate.id !== conversation.id);
        if (replacement) {
          void navigate({
            to: '/app/c/$conversationId',
            params: { conversationId: replacement.id },
            replace: true,
          });
        } else {
          void navigate({ to: '/app/new', replace: true });
        }
      }
    } catch (error) {
      setDeleteChatState({
        conversationId: conversation.id,
        status: 'error',
        error: error instanceof Error ? error.message : 'The chat could not be deleted.',
      });
    }
  };

  const chatLinks = (showsAllChats ? allChats : recentChats).map((conversation) => {
    const displayTitle = conversation.isStreaming
      ? conversation.title.replace(/[.\u2026]+\s*$/, '').trimEnd()
      : conversation.title;
    const isActive = Boolean(
      matchRoute({ to: '/app/c/$conversationId', params: { conversationId: conversation.id } }),
    );
    const inlineDelete = deleteChatState?.conversationId === conversation.id
      ? deleteChatState
      : null;
    return (
      <div
        key={conversation.id}
        className={styles.chatRow}
        data-active={isActive || undefined}
        data-confirming={Boolean(inlineDelete) || undefined}
      >
        <Link
          to="/app/c/$conversationId"
          params={{ conversationId: conversation.id }}
          className={`${styles.item} ${styles.chatItem}`}
          data-active={isActive || undefined}
          data-streaming={conversation.isStreaming || undefined}
          aria-label={conversation.isStreaming ? `${conversation.title}, working` : conversation.title}
          title={conversation.preview ?? conversation.title}
        >
          <span className={styles.itemIcon}>
            <Icon name="message-circle" size={17} />
          </span>
          <span className={styles.conversationLabel}>
            <span className={styles.itemLabel}>{displayTitle}</span>
            {conversation.isStreaming ? (
              <span className={styles.runningDots} aria-hidden="true">
                <span>.</span><span>.</span><span>.</span>
              </span>
            ) : null}
          </span>
        </Link>
        {inlineDelete ? (
          <div
            className={styles.deleteConfirmation}
            data-status={inlineDelete.status}
            title={inlineDelete.error}
            role="group"
            aria-label={`Confirm deletion of ${conversation.title}`}
          >
            <span className={styles.deleteConfirmationLabel}>
              {inlineDelete.status === 'deleting'
                ? 'Deleting…'
                : inlineDelete.status === 'error'
                  ? inlineDelete.error === 'The Resonance server is unavailable.'
                    ? 'Server unavailable'
                    : 'Couldn’t delete'
                  : 'Delete chat?'}
            </span>
            <button
              type="button"
              className={styles.cancelDelete}
              onClick={() => setDeleteChatState(null)}
              disabled={inlineDelete.status === 'deleting'}
              aria-label="Cancel deletion"
              title="Cancel"
            >
              <Icon name="x" size={14} />
            </button>
            <button
              type="button"
              className={styles.confirmDelete}
              onClick={() => void handleDeleteConversation(conversation)}
              disabled={inlineDelete.status === 'deleting'}
            >
              {inlineDelete.status === 'error' ? 'Retry' : 'Delete'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.deleteChat}
            onClick={() => setDeleteChatState({ conversationId: conversation.id, status: 'confirming' })}
            aria-label={`Delete ${conversation.title}`}
            title="Delete chat"
          >
            <Icon name="x" size={15} />
          </button>
        )}
      </div>
    );
  });

  const favoriteLinks = (showsAllFavorites ? favorites.data?.favorites : favorites.data?.favorites.slice(0, 4))?.map(favorite => (
    <div key={favorite.id} className={styles.chatRow}>
      <Link to="/app/o/$object/$recordId"
        params={{ object: favorite.objectNamePlural, recordId: favorite.recordId }}
        className={`${styles.item} ${styles.chatItem}`}
        data-active={Boolean(matchRoute({ to: '/app/o/$object/$recordId', params: { object: favorite.objectNamePlural, recordId: favorite.recordId } }))}
        title={`${favorite.displayName} · ${favorite.objectLabel}`}>
        <span className={styles.itemIcon}><Icon name={favorite.iconKey} size={17} /></span>
        <span className={styles.itemLabel}>{favorite.displayName}</span>
      </Link>
      <button type="button" className={styles.deleteChat} disabled={setFavorite.isPending}
        aria-label={`Remove ${favorite.displayName} from favorites`} title="Remove favorite"
        onClick={() => setFavorite.mutate({ recordId: favorite.recordId, favorite: false })}>
        <Icon name="x" size={15} />
      </button>
    </div>
  ));
  const favoriteSection = hasFavorites ? (
    <section className={styles.section} aria-label="Favorites">
      <div className={styles.sectionHeading}>
        <h2 className={styles.sectionTitle}>Favorites</h2>
        {showsAllFavorites || (favorites.data?.favorites.length ?? 0) > 0 ? (
          <button type="button" className={styles.seeAll} onClick={() => { setShowsAllFavorites(!showsAllFavorites); setFavorite.reset(); }}>
            {showsAllFavorites ? 'Back' : 'See all'}
          </button>
        ) : null}
      </div>
      {favorites.isError ? <p className={styles.empty} role="alert">Couldn’t load favorites. <button type="button" onClick={() => void favorites.refetch()}>Retry</button></p>
        : favorites.isLoading ? <p className={styles.empty}>Loading…</p>
        : favoriteLinks}
      {setFavorite.isError && <p className={styles.empty} role="alert">Couldn’t remove favorite. Try again.</p>}
    </section>
  ) : null;

  return (
    <nav className={styles.rail} aria-label="Primary">
      <div className={styles.brand}>
        <ResonanceMark size={22} tone="theme" className={styles.mark} />
        Resonance
      </div>

      <div className={styles.scroll}>
        {showsAllFavorites && hasFavorites ? favoriteSection : showsAllChats ? (
          <section className={`${styles.section} ${styles.allChats}`}>
            <div className={styles.sectionHeading}>
              <h2 className={styles.sectionTitle}>Recent chats</h2>
              <button type="button" className={styles.seeAll} onClick={() => setShowsAllChats(false)}>
                Back
              </button>
            </div>
            {allChats.length === 0 ? <p className={styles.empty}>No conversations yet.</p> : chatLinks}
          </section>
        ) : (
          <>
            <section className={styles.section}>
              <Link
                className={styles.item}
                to="/app/brief"
                data-active={Boolean(matchRoute({ to: '/app/brief' }))}
              >
                <span className={styles.itemIcon}>
                  <Icon name="sparkles" size={17} />
                </span>
                <span className={styles.itemLabel}>Home</span>
                {brief.data?.has_unseen ? <span className={styles.newBadge}>New</span> : null}
              </Link>
              <Link
                className={styles.item}
                to="/app/new"
                data-active={Boolean(matchRoute({ to: '/app/new' }))}
              >
                <span className={styles.itemIcon}>
                  <Icon name="plus" size={17} />
                </span>
                <span className={styles.itemLabel}>New chat</span>
              </Link>
              <Link
                className={styles.item}
                to="/app/activity"
                data-active={Boolean(matchRoute({ to: '/app/activity' }))}
              >
                <span className={styles.itemIcon}>
                  <Icon name="history-toggle" size={17} />
                </span>
                <span className={styles.itemLabel}>Timeline</span>
              </Link>
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeading}>
                <h2 className={styles.sectionTitle}>Recent chats</h2>
                {allChats.length > 0 ? (
                  <button type="button" className={styles.seeAll} onClick={() => setShowsAllChats(true)}>
                    See all
                  </button>
                ) : null}
              </div>
              {recentChats.length === 0 ? <p className={styles.empty}>No conversations yet.</p> : chatLinks}
            </section>

            {favoriteSection}

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Workspace</h2>
              {objects.length === 0 ? (
                <p className={styles.empty}>{schema.isLoading ? 'Loading…' : 'No objects yet.'}</p>
              ) : (
                objects.map((object) => (
                  <Link
                    key={object.id}
                    to="/app/o/$object"
                    params={{ object: object.namePlural }}
                    className={styles.item}
                    data-active={Boolean(matchRoute({ to: '/app/o/$object', params: { object: object.namePlural } }))}
                  >
                    <span className={styles.itemIcon}>
                      <Icon name={object.icon ?? 'box'} size={17} />
                    </span>
                    <span className={styles.itemLabel}>{object.labelPlural}</span>
                    <WorkspaceCount
                      count={object.recordCount}
                      isIncreased={increasedObjectIds.has(object.id)}
                    />
                  </Link>
                ))
              )}
              <Link to="/app/email/automations" className={styles.item} data-active={Boolean(matchRoute({to:'/app/email',fuzzy:true}))}>
                <span className={styles.itemIcon}><Icon name="mail" size={17}/></span><span className={styles.itemLabel}>Email Automations</span><NavigationDot label="Email Automations" kind="sequences" status={navigationStatus}/>
              </Link>
              <Link to="/app/connections" className={styles.item} data-active={Boolean(matchRoute({to:'/app/connections',fuzzy:true}))}><span className={styles.itemIcon}><Icon name="plug" size={17}/></span><span className={styles.itemLabel}>Connections</span><NavigationDot label="Connections" kind="connections" status={navigationStatus}/></Link>
            </section>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.account}>
          <div className={styles.accountText}>
            <span className={styles.accountName}>{accountName}</span>
            <span className={styles.accountSubtitle}>{accountSubtitle}</span>
          </div>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setAppearance(appearance === 'dark' ? 'light' : 'dark')}
            aria-label={`Switch to ${appearance === 'dark' ? 'light' : 'dark'} mode`}
          >
            <Icon name={appearance === 'dark' ? 'sun' : 'moon'} size={20} />
          </button>
        </div>
        <Link
          to="/app/settings"
          className={styles.iconButton}
          data-active={Boolean(matchRoute({ to: '/app/settings', fuzzy: true }))}
          aria-label="Settings"
        >
          <Icon name="settings" size={20} />
        </Link>
      </div>
    </nav>
  );
}

function WorkspaceCount({ count, isIncreased }: { count: number; isIncreased: boolean }) {
  return (
    <span
      className={styles.itemCount}
      data-increased={isIncreased || undefined}
      aria-label={`${count} records`}
    >
      {count}
    </span>
  );
}
